import React, { useRef, useCallback, useMemo, useState } from "react";
import {
  CarouselHeader,
  HeaderText,
  ArrowButton,
  CarouselContainer,
  CarouselTrack,
} from "./RoundMaterialCarousel.styles";
import {
  useCarouselAnimation,
  useContainerSize,
  useMobileDetection,
  useCardExpansion,
} from "./carouselHooks";
import {
  getCardAtIndex,
  getCenteredIndex,
  CAROUSEL_CARDS,
  ROUND_CARD_WIDTH_PX,
  ROUND_CARD_GAP_PX,
  ANIM_DURATION_MS,
  MOBILE_ANIM_DURATION_MS,
  ANGLE_STEP_DEG,
  ARC_RADIUS_MULTIPLIER,
  CARD_WRAPPER_STYLE,
} from "./carouselShared";
import CarouselCard from "./CarouselCard";
import { MaterialThumbnail } from "./RoundMaterialCarousel.styles";
import ExpandedCardOverlay from "./ExpandedCardOverlay";
import {
  ExpandedOverlay,
  ExpandedCard,
  PreviewOverlay,
  PreviewImage,
  PreviewCloseButton,
} from "./RoundMaterialCarousel.styles";

const RoundMaterialCarousel: React.FC = () => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  const { scrollOffset, isAnimating, animateTo } = useCarouselAnimation();
  const containerSize = useContainerSize(trackRef);
  const isMobile = useMobileDetection();
  const {
    expandedIndex,
    overlayShown,
    expandedCardRef,
    firstRectRef,
    lastRectRef,
    handleClose,
  } = useCardExpansion();

  // State for preview popup
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const previewCard = useMemo(
    () =>
      previewIndex !== null
        ? getCardAtIndex(previewIndex, CAROUSEL_CARDS)
        : null,
    [previewIndex],
  );

  // Memoize card width calculation
  const totalCardWidth = useMemo(
    () => ROUND_CARD_WIDTH_PX + ROUND_CARD_GAP_PX,
    [],
  );

  // Mobile-specific card dimensions
  const MOBILE_CARD_WIDTH_PX = 280;

  // Memoize hero index
  const heroIndex = useMemo(
    () =>
      getCenteredIndex(scrollOffset, ROUND_CARD_WIDTH_PX, ROUND_CARD_GAP_PX),
    [scrollOffset],
  );

  // Calculate arc position for each card (infinite scrolling)
  // Cards are positioned along the top half of a circle (arc)
  // Center card is at the top of the arc, cards spread to the sides
  const getArcPosition = useCallback(
    (index: number) => {
      const centerX = containerSize.width / 2;
      const centerY = containerSize.height * 0.85; // Arc center below the container for top-half arc

      // Calculate the arc radius based on container size
      const arcRadius =
        Math.min(containerSize.width, containerSize.height) *
        ARC_RADIUS_MULTIPLIER;

      // Calculate the centered index based on scroll offset
      const centeredIndex = scrollOffset / totalCardWidth;

      // Calculate angle offset from center (in degrees)
      // Negative offset means to the left (angle > 90°), positive means to the right (angle < 90°)
      const indexOffset = index - centeredIndex;
      const angleOffsetDeg = indexOffset * ANGLE_STEP_DEG;

      // Base angle is 90° (top of arc), offset determines position along arc
      // angle = 90° - offset (so positive offset moves right/clockwise, negative moves left/counter-clockwise)
      const angleDeg = 90 - angleOffsetDeg;
      const angleRad = (angleDeg * Math.PI) / 180;

      // Calculate x, y using parametric circle equations
      // x = centerX + radius * cos(angle)
      // y = centerY - radius * sin(angle) (negative because screen y increases downward)
      const x = centerX + arcRadius * Math.cos(angleRad);
      // Add vertical offset to push cards down so they don't overflow the top (percentage of container height)
      const verticalOffset = containerSize.height * 1.5;
      const y = centerY - arcRadius * Math.sin(angleRad) + verticalOffset;

      // Calculate rotation for fan shape - cards fan outward from center
      // Positive angleOffsetDeg (right side) = positive rotation (clockwise)
      // Negative angleOffsetDeg (left side) = negative rotation (counter-clockwise)
      const rotationDeg = angleOffsetDeg * 0.8; // Fan outward from center

      // Only render cards that are within a reasonable angle range
      // Cards too far from center (beyond ~80° offset) are not visible
      const isVisible = Math.abs(angleOffsetDeg) < 80;

      // Z-index: cards closer to center (top) should be on top
      const zIndex = Math.round(100 - Math.abs(angleOffsetDeg));

      // Opacity and scale: cards at edges can be slightly faded/smaller
      const distanceFromCenter = Math.abs(angleOffsetDeg);
      const opacity = Math.max(0.3, 1 - distanceFromCenter / 100);
      const scale = Math.max(0.7, 1 - distanceFromCenter / 200);

      return {
        x,
        y,
        opacity,
        scale,
        zIndex,
        isVisible,
        rotationDeg,
      };
    },
    [containerSize.width, containerSize.height, totalCardWidth, scrollOffset],
  );

  const handleThumbnailClick = useCallback(
    (index: number) => {
      if (isAnimating) return;

      const targetOffset = index * totalCardWidth;

      // Scroll to center the card
      if (index !== heroIndex) {
        animateTo(targetOffset, ANIM_DURATION_MS);
      }

      // Open preview popup with the preview image
      setPreviewIndex(index);
    },
    [isAnimating, heroIndex, totalCardWidth, animateTo],
  );

  const handleClosePreview = useCallback(() => {
    setPreviewIndex(null);
  }, []);

  const handleArrowLeft = useCallback(() => {
    if (isAnimating) return;
    const nextIndex = heroIndex - 1;
    const targetOffset = nextIndex * totalCardWidth;
    animateTo(targetOffset, ANIM_DURATION_MS);
  }, [isAnimating, heroIndex, totalCardWidth, animateTo]);

  const handleArrowRight = useCallback(() => {
    if (isAnimating) return;
    const nextIndex = heroIndex + 1;
    const targetOffset = nextIndex * totalCardWidth;
    animateTo(targetOffset, ANIM_DURATION_MS);
  }, [isAnimating, heroIndex, totalCardWidth, animateTo]);

  // Calculate horizontal position for mobile cards (infinite scrolling)
  const getMobileHorizontalPosition = useCallback(
    (index: number) => {
      const centerX = containerSize.width / 2;
      const centerY = containerSize.height / 2;

      // Calculate x position based on index and scrollOffset
      // Use totalCardWidth for consistency with desktop scrollOffset calculation
      const x = centerX + index * totalCardWidth - scrollOffset;
      const y = centerY;

      // Only render cards that are within viewport range (with buffer)
      const viewportBuffer = containerSize.width * 2;
      const isVisible =
        x > -viewportBuffer && x < containerSize.width + viewportBuffer;

      return {
        x,
        y,
        opacity: 1.0,
        scale: 1.0,
        zIndex: 1,
        isVisible,
      };
    },
    [containerSize.width, containerSize.height, totalCardWidth, scrollOffset],
  );

  // Memoize visible card range for mobile
  const mobileVisibleCardRange = useMemo(() => {
    if (!isMobile) return { startIndex: 0, endIndex: 0 };
    const centerIndex = Math.round(scrollOffset / totalCardWidth);
    // Render a few cards on each side for smooth sliding
    const visibleRange = 2;
    return {
      startIndex: centerIndex - visibleRange,
      endIndex: centerIndex + visibleRange,
    };
  }, [isMobile, scrollOffset, totalCardWidth]);

  // Memoize mobile cards to render
  const mobileCardsToRender = useMemo(() => {
    if (!isMobile) return [];
    const cardsToRender = [];
    for (
      let index = mobileVisibleCardRange.startIndex;
      index <= mobileVisibleCardRange.endIndex;
      index++
    ) {
      const card = getCardAtIndex(index, CAROUSEL_CARDS);
      const { x, y, opacity, zIndex, isVisible } =
        getMobileHorizontalPosition(index);

      if (!isVisible) continue;

      cardsToRender.push(
        <div
          key={`mobile-wrapper-${index}`}
          style={{
            ...CARD_WRAPPER_STYLE,
            left: `${x}px`,
            top: `${y}px`,
            transform: `translate(-50%, -50%)`,
            width: `${MOBILE_CARD_WIDTH_PX}px`,
            height: "432px",
            zIndex: index === heroIndex ? 2 : zIndex,
            transition: `left ${MOBILE_ANIM_DURATION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
          }}
        >
          <CarouselCard
            card={card}
            index={index}
            opacity={opacity}
            onClick={handleThumbnailClick}
            onRef={(el) => (itemRefs.current[index] = el)}
            thumbnailStyle={{
              width: `${MOBILE_CARD_WIDTH_PX}px`,
              height: "432px",
              pointerEvents: "auto",
            }}
            ThumbnailComponent={MaterialThumbnail}
          />
        </div>,
      );
    }
    return cardsToRender;
  }, [
    isMobile,
    mobileVisibleCardRange,
    heroIndex,
    getMobileHorizontalPosition,
    handleThumbnailClick,
  ]);

  // Memoize visible card range for desktop
  const visibleCardRange = useMemo(() => {
    if (isMobile) return { startIndex: 0, endIndex: 0 };
    const centerIndex = Math.round(scrollOffset / totalCardWidth);
    // For arc, we need fewer visible cards (limited by angle range)
    const visibleRange = Math.ceil(80 / ANGLE_STEP_DEG) + 2; // Based on max angle
    return {
      startIndex: centerIndex - visibleRange,
      endIndex: centerIndex + visibleRange,
    };
  }, [isMobile, scrollOffset, totalCardWidth]);

  // Memoize desktop cards to render
  const desktopCardsToRender = useMemo(() => {
    if (isMobile) return [];
    const cardsToRender = [];
    for (
      let index = visibleCardRange.startIndex;
      index <= visibleCardRange.endIndex;
      index++
    ) {
      const card = getCardAtIndex(index, CAROUSEL_CARDS);
      const { x, y, opacity, scale, zIndex, isVisible, rotationDeg } =
        getArcPosition(index);

      if (!isVisible) continue;

      cardsToRender.push(
        <div
          key={`wrapper-${index}`}
          style={{
            ...CARD_WRAPPER_STYLE,
            left: `${x}px`,
            top: `${y}px`,
            transform: `translate(-50%, -50%) rotate(${rotationDeg}deg) scale(${scale})`,
            width: "300px",
            height: "450px",
            // Keep hero slightly higher than siblings, but still below side fades & header
            zIndex: index === heroIndex ? zIndex + 10 : zIndex,
            transition: "transform 0.1s ease-out",
          }}
        >
          <CarouselCard
            card={card}
            index={index}
            opacity={opacity}
            onClick={handleThumbnailClick}
            onRef={(el) => (itemRefs.current[index] = el)}
            ThumbnailComponent={MaterialThumbnail}
          />
        </div>,
      );
    }
    return cardsToRender;
  }, [
    isMobile,
    visibleCardRange,
    heroIndex,
    getArcPosition,
    handleThumbnailClick,
  ]);

  return (
    <>
      <CarouselHeader>
        <ArrowButton
          onClick={handleArrowLeft}
          aria-label="Previous material"
          disableRipple
        >
          &lt;
        </ArrowButton>
        <HeaderText>See what others have designed</HeaderText>
        <ArrowButton
          onClick={handleArrowRight}
          aria-label="Next material"
          disableRipple
        >
          &gt;
        </ArrowButton>
      </CarouselHeader>
      <CarouselContainer
        sx={isMobile ? { height: "auto", minHeight: "450px" } : {}}
      >
        <CarouselTrack ref={trackRef}>
          {isMobile ? mobileCardsToRender : desktopCardsToRender}
        </CarouselTrack>
      </CarouselContainer>

      <ExpandedCardOverlay
        expandedIndex={expandedIndex}
        overlayShown={overlayShown}
        expandedCardRef={expandedCardRef}
        firstRectRef={firstRectRef}
        lastRectRef={lastRectRef}
        onClose={handleClose}
        ExpandedOverlayComponent={ExpandedOverlay}
        ExpandedCardComponent={ExpandedCard}
      />

      {/* Preview Popup */}
      {previewCard && (
        <PreviewOverlay
          type="button"
          onClick={handleClosePreview}
          aria-label="Close preview"
        >
          <PreviewImage onClick={(e) => e.stopPropagation()}>
            <PreviewCloseButton
              onClick={(e) => {
                e.stopPropagation();
                handleClosePreview();
              }}
              aria-label="Close preview"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <title>Close</title>
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </PreviewCloseButton>
            <img src={previewCard.preview} alt={previewCard.alt} />
          </PreviewImage>
        </PreviewOverlay>
      )}
    </>
  );
};

export default RoundMaterialCarousel;
